// src/app/components/chat/ColleaguePicker.tsx
//
// Lista buscável de colegas da escola (via chatService.listColleagues), com
// suporte a seleção única ou múltipla. Reutilizado por NewChatDialog (criar
// grupo/DM) e ChatMemberPanel (adicionar membro a um grupo custom) — mesmo
// padrão visual de AssigneePopover.tsx (board-completo/card-detail).

import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Skeleton } from '../ui/skeleton';
import { chatService, type ChatUserMini } from '../../services/chatService';
import { getUserInitials } from './chatUtils';

export interface ColleaguePickerProps {
  /** ids já selecionados (multi) ou já presentes no grupo (para filtrar fora da lista) */
  excludeIds?: number[];
  onSelect: (user: ChatUserMini) => void;
  /** ids marcados com um Check — omitir para não marcar nenhum (uso single-select) */
  selectedIds?: number[];
  emptyLabel?: string;
}

export default function ColleaguePicker({
  excludeIds = [],
  onSelect,
  selectedIds = [],
  emptyLabel = 'Nenhum colega encontrado.',
}: ColleaguePickerProps) {
  const [colleagues, setColleagues] = useState<ChatUserMini[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    chatService
      .listColleagues()
      .then((list) => {
        if (!cancelled) setColleagues(list);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
          setColleagues([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const excludeSet = new Set(excludeIds);
  const selectedSet = new Set(selectedIds);
  const visible = (colleagues ?? []).filter((u) => !excludeSet.has(u.id));

  return (
    <Command>
      <CommandInput placeholder="Buscar por nome..." />
      <CommandList>
        {colleagues === null ? (
          <div className="p-2 space-y-1.5">
            <Skeleton className="h-8 w-full rounded-sm dark:bg-white/10" />
            <Skeleton className="h-8 w-full rounded-sm dark:bg-white/10" />
            <Skeleton className="h-8 w-full rounded-sm dark:bg-white/10" />
          </div>
        ) : (
          <>
            <CommandEmpty className="py-6 text-xs text-slate-500 dark:text-slate-400">
              {loadFailed ? 'Erro ao carregar colegas.' : visible.length === 0 ? emptyLabel : 'Nenhum resultado.'}
            </CommandEmpty>
            <CommandGroup>
              {visible.map((u) => (
                <CommandItem
                  key={u.id}
                  value={`${u.full_name} ${u.username}`}
                  onSelect={() => onSelect(u)}
                  className="cursor-pointer"
                >
                  <div className="size-6 rounded-full bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                    {getUserInitials(u)}
                  </div>
                  <span className="flex-1 min-w-0 truncate text-slate-800 dark:text-slate-200">{u.full_name || u.username}</span>
                  {selectedSet.has(u.id) && <Check className="size-4 text-indigo-600 dark:text-indigo-400" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </Command>
  );
}
